/* Nautilus CRO - 2026-05-13 - Display-only currency conversion */
(function () {
  'use strict';

  const COUNTRY_CURRENCY = {
    // Euro
    AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR',
    FI: 'EUR', FR: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LT: 'EUR',
    LU: 'EUR', LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR',
    SK: 'EUR', AD: 'EUR', MC: 'EUR', SM: 'EUR', VA: 'EUR',
    // Major
    US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD', CH: 'CHF', LI: 'CHF',
    NZ: 'NZD',
    // Asia-Pacific
    JP: 'JPY', CN: 'CNY', IN: 'INR', SG: 'SGD', HK: 'HKD',
    KR: 'KRW', TW: 'TWD', MY: 'MYR', ID: 'IDR', TH: 'THB', PH: 'PHP',
    // Americas
    BR: 'BRL', MX: 'MXN',
    // Scandinavia
    SE: 'SEK', NO: 'NOK', DK: 'DKK',
    // Eastern Europe
    PL: 'PLN', CZ: 'CZK', HU: 'HUF',
    // Middle East / Turkey / Africa
    TR: 'TRY', SA: 'SAR', AE: 'AED', QA: 'QAR', KW: 'KWD',
    BH: 'BHD', OM: 'OMR', ZA: 'ZAR', NG: 'NGN', EG: 'EGP',
  };

  const SUPPORTED = new Set([
    'EUR', 'USD', 'GBP', 'AUD', 'CAD', 'CHF', 'NZD',
    'JPY', 'CNY', 'INR', 'SGD', 'HKD', 'KRW', 'TWD', 'MYR', 'IDR', 'THB', 'PHP',
    'BRL', 'MXN',
    'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF',
    'TRY', 'SAR', 'AED', 'QAR', 'KWD', 'BHD', 'OMR',
    'ZAR', 'NGN', 'EGP',
  ]);

  const RATES_KEY = 'nc_fx_v3';
  const PREF_KEY = 'nc_currency_v1';
  const CACHE_TTL = 3_600_000;

  // Fallback rates (EUR base) — used if API is unreachable
  const FALLBACK_RATES = {
    EUR: 1,    USD: 1.08,  GBP: 0.85,  AUD: 1.65,  CAD: 1.47,  CHF: 0.94,  NZD: 1.78,
    JPY: 163,  CNY: 7.8,   INR: 90,    SGD: 1.46,  HKD: 8.4,   KRW: 1470,  TWD: 35.6,
    MYR: 5.0,  IDR: 17500, THB: 38,    PHP: 62,
    BRL: 6.3,  MXN: 21,
    SEK: 11.5, NOK: 11.8,  DKK: 7.46,  PLN: 4.28,  CZK: 25.3,  HUF: 400,
    TRY: 37,   SAR: 4.05,  AED: 3.97,  QAR: 3.93,  KWD: 0.332, BHD: 0.406, OMR: 0.416,
    ZAR: 20,   NGN: 1728,  EGP: 53,
  };

  let rates = FALLBACK_RATES;
  let activeCurrency = 'EUR';
  let isApplying = false;

  // ── Intercept localization form.submit() ──────────────────────────────────
  // The Atelier theme calls form.submit() programmatically, bypassing submit events.
  // Patching the prototype is the only guaranteed interception point.

  const _nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    if (this.id === 'LocalizationForm') {
      const countryInput = this.querySelector('[name="country_code"]');
      if (countryInput && countryInput.value) {
        const currency = COUNTRY_CURRENCY[countryInput.value] || 'EUR';
        try { localStorage.setItem(PREF_KEY, currency); } catch (_) {}
      }
    }
    return _nativeSubmit.call(this);
  };

  // ── Rate fetching ─────────────────────────────────────────────────────────

  function loadCachedRates() {
    try {
      const raw = localStorage.getItem(RATES_KEY);
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts < CACHE_TTL) return data;
    } catch (_) {}
    return null;
  }

  async function fetchRates() {
    const cached = loadCachedRates();
    if (cached) return cached;
    try {
      // Currencies not on frankfurter.app are derived from the live USD rate via their USD pegs
      const symbols = 'USD,GBP,AUD,CAD,CHF,NZD,JPY,CNY,INR,SGD,HKD,KRW,MYR,IDR,THB,PHP,BRL,MXN,SEK,NOK,DKK,PLN,CZK,HUF,TRY,ZAR';
      const res = await fetch(`https://api.frankfurter.app/latest?base=EUR&symbols=${symbols}`);
      if (!res.ok) throw new Error('bad response');
      const json = await res.json();
      const usd = json.rates.USD;
      const data = {
        EUR: 1,
        ...json.rates,
        SAR: +(usd * 3.75).toFixed(4),     // fixed peg
        AED: +(usd * 3.6725).toFixed(4),   // fixed peg
        QAR: +(usd * 3.64).toFixed(4),     // fixed peg
        KWD: +(usd * 0.307).toFixed(4),    // managed float, close to fixed
        BHD: +(usd * 0.376).toFixed(4),    // fixed peg
        OMR: +(usd * 0.3845).toFixed(4),   // fixed peg
        NGN: +(usd * 1600).toFixed(2),     // floating, approximate
        EGP: +(usd * 49).toFixed(2),       // floating, approximate
        TWD: +(usd * 33).toFixed(2),       // floating, approximate
      };
      try { localStorage.setItem(RATES_KEY, JSON.stringify({ ts: Date.now(), data })); } catch (_) {}
      return data;
    } catch (_) {
      return FALLBACK_RATES;
    }
  }

  // ── Currency detection ────────────────────────────────────────────────────

  async function detectCurrency() {
    const saved = localStorage.getItem(PREF_KEY);
    if (saved && SUPPORTED.has(saved)) return saved;
    try {
      const res = await fetch('https://ipapi.co/json/');
      const json = await res.json();
      return COUNTRY_CURRENCY[json.country_code] || 'EUR';
    } catch (_) {
      return 'EUR';
    }
  }

  // ── Price parsing ─────────────────────────────────────────────────────────

  function parseEurAmount(text) {
    let s = text.replace(/[^0-9.,]/g, '').trim();
    if (!s) return NaN;

    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');

    if (lastComma > lastDot) {
      // European decimal comma: 29,99 or 1.299,99
      s = s.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      // Standard decimal dot: 29.99 or 1,299.99
      s = s.replace(/,/g, '');
    } else if (lastComma !== -1) {
      // Single separator — thousands if 3 digits follow, else decimal
      s = s.substring(lastComma + 1).length === 3
        ? s.replace(',', '')
        : s.replace(',', '.');
    }

    return parseFloat(s);
  }

  function formatPrice(amount, currency) {
    try {
      return new Intl.NumberFormat(navigator.language || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return amount.toFixed(2);
    }
  }

  // ── DOM conversion ────────────────────────────────────────────────────────

  const PRICE_SEL = '.price, .compare-at-price';

  function applyConversion() {
    if (activeCurrency === 'EUR') return;
    isApplying = true;
    const rate = rates[activeCurrency] || 1;

    document.querySelectorAll(PRICE_SEL).forEach((el) => {
      const text = el.textContent.trim();
      if (!text) return;

      if (!el.dataset.ncEur) {
        const eur = parseEurAmount(text);
        if (isNaN(eur) || eur <= 0) return;
        el.dataset.ncEur = eur;
      }

      const eur = parseFloat(el.dataset.ncEur);
      if (isNaN(eur)) return;
      el.textContent = formatPrice(eur * rate, activeCurrency);
    });

    showNote();
    setTimeout(() => { isApplying = false; }, 0);
  }

  // ── Disclaimer note on product pages ─────────────────────────────────────

  function showNote() {
    if (!window.location.pathname.includes('/products/')) return;
    if (document.getElementById('nc-price-note')) return;
    const container = document.querySelector('[ref="priceContainer"]');
    if (!container) return;
    const note = document.createElement('p');
    note.id = 'nc-price-note';
    note.className = 'nc-price-note';
    note.textContent = 'Prices are approximate — you will be charged in EUR at checkout';
    container.insertAdjacentElement('afterend', note);
  }

  // ── MutationObserver: handle variant/cart price updates ──────────────────

  let mutTimer = null;

  const observer = new MutationObserver((mutations) => {
    if (isApplying || activeCurrency === 'EUR') return;
    let needsUpdate = false;
    for (const m of mutations) {
      const t = m.target;
      if (t.matches && t.matches(PRICE_SEL) && t.dataset.ncEur) {
        delete t.dataset.ncEur;
        needsUpdate = true;
      } else if (m.addedNodes.length) {
        needsUpdate = true;
      }
    }
    if (needsUpdate) {
      clearTimeout(mutTimer);
      mutTimer = setTimeout(applyConversion, 150);
    }
  });

  // ── Init ──────────────────────────────────────────────────────────────────

  async function init() {
    const [fetchedRates, currency] = await Promise.all([fetchRates(), detectCurrency()]);
    rates = fetchedRates;
    activeCurrency = currency;

    if (activeCurrency === 'EUR') return;

    applyConversion();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
